import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  from,
  Observable,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client/link/utils/fromPromise';
import requestTracker from './requestTracker'; // Import the shared RequestTracker logic

let apolloClient = null;

export function getApolloClientMfe() {
  if (!apolloClient) {
    throw new Error('ApolloClient has not been initialized yet.');
  }
  return apolloClient;
}

// Variables to handle concurrent token refreshes
let isRefreshing = false;
let pendingRequests = [];

// Helper to resolve queued requests once a new token is obtained
const resolvePendingRequests = (newToken) => {
  pendingRequests.forEach((callback) => callback(newToken));
  pendingRequests = [];
};

/**
 * Creates an Apollo Client instance.
 *
 * @param {Object} options - The options object.
 * @param {string} options.uri - The API URI.
 * @param {string} options.initialToken - The initial access token.
 * @param {Function} options.refreshTokenFunc - A function that returns a Promise resolving to a new token.
 * @param {string} [options.accessTokenStorageKey] - If provided, will fetch token from localStorage using this key.
 * @returns {ApolloClient} The Apollo Client instance.
 */
const createMfeApolloClient = ({
  uri,
  initialToken,
  refreshTokenFunc,
  accessTokenStorageKey,
  mfeType,
}) => {
  // Function to get the current token from localStorage or use the initial token
  const getToken = () => {
    if (accessTokenStorageKey && typeof localStorage !== 'undefined') {
      const storedToken = localStorage.getItem(accessTokenStorageKey);
      return storedToken || initialToken;
    }
    return initialToken;
  };

  let currentToken = getToken();

  const mfeUri = new URL(uri);
  mfeUri.searchParams.append('isMfe', 'true');
  if (mfeType) {
    mfeUri.searchParams.append('mfeType', mfeType);
  }

  const primaryHttpLink = new HttpLink({
    uri: mfeUri.toString(),
    headers: {
      authorization: currentToken ? `Bearer ${currentToken}` : '',
    },
  });

  // Enhanced error link that intercepts 401 errors and refreshes the token
  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          )
        );
      }

      if (networkError) {
        console.log(`[Network error]: ${networkError}`);
      }

      // Check for 401 error (adjust based on your error shape)
      const status =
        networkError?.statusCode ||
        (networkError?.response && networkError.response.status);
      if (status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;
          return fromPromise(
            refreshTokenFunc()
              .then((newToken) => {
                isRefreshing = false;
                currentToken = newToken; // Update the token

                // Save the new token to localStorage if accessTokenStorageKey is provided
                if (
                  accessTokenStorageKey &&
                  typeof localStorage !== 'undefined'
                ) {
                  localStorage.setItem(accessTokenStorageKey, newToken);
                }

                resolvePendingRequests(newToken);
                return newToken;
              })
              .catch((error) => {
                isRefreshing = false; // Ensure flag is reset on error
                pendingRequests = [];
                resolvePendingRequests(null); // Resolve with null to indicate error
                throw error;
              })
          ).flatMap((newToken) => {
            // Only proceed if we got a valid token
            if (!newToken) {
              return new Observable((observer) => {
                observer.error(new Error('Token refresh failed'));
              });
            }
            // Retry the failed operation with the new token in its headers
            operation.setContext(({ headers = {} }) => ({
              headers: {
                ...headers,
                authorization: newToken ? `Bearer ${newToken}` : '',
              },
            }));
            return forward(operation);
          });
        } else {
          // Queue the request until token refresh is complete
          return fromPromise(
            new Promise((resolve, reject) => {
              pendingRequests.push((token) => {
                if (token) {
                  resolve(token);
                } else {
                  // If token is null, the refresh failed
                  reject(new Error('Token refresh failed'));
                }
              });
            })
          ).flatMap((newToken) => {
            // Skip if we didn't get a valid token
            if (!newToken) {
              return new Observable((observer) => {
                observer.error(new Error('Token refresh failed'));
              });
            }

            operation.setContext(({ headers = {} }) => ({
              headers: {
                ...headers,
                authorization: newToken ? `Bearer ${newToken}` : '',
              },
            }));
            return forward(operation);
          });
        }
      }
    }
  );

  // Tracking link to monitor request status
  const trackingLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      const requestId = requestTracker.addRequest();

      const subscription = forward(operation).subscribe({
        next: (result) => observer.next(result),
        error: (error) => {
          try {
            requestTracker.removeRequest(requestId);
            observer.error(error);
          } catch (handlingError) {
            console.error('Error in error handler:', handlingError);
            observer.error(error); // Still try to propagate the original error
          }
        },
        complete: () => {
          try {
            requestTracker.removeRequest(requestId);
            observer.complete();
          } catch (handlingError) {
            console.error('Error in complete handler:', handlingError);
            observer.complete(); // Still try to complete
          }
        },
      });

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          requestTracker.removeRequest(requestId);
        }
      };
    });
  });

  // Token link to set the Authorization header with the current token
  const tokenLink = new ApolloLink((operation, forward) => {
    // Always check for latest token from localStorage if accessTokenStorageKey is provided
    if (accessTokenStorageKey && typeof localStorage !== 'undefined') {
      const storedToken = localStorage.getItem(accessTokenStorageKey);
      if (storedToken) {
        currentToken = storedToken;
      }
    }

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        authorization: currentToken ? `Bearer ${currentToken}` : '',
      },
    }));
    return forward(operation);
  });

  apolloClient = new ApolloClient({
    link: from([errorLink, trackingLink, tokenLink, primaryHttpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-first', // or 'cache-and-network'
      },
    },
  });

  return apolloClient;
};

export { createMfeApolloClient };
